import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, ArrowLeft, Clock, User } from 'lucide-react';

const BLOG_POSTS = [
  {
    id: 'why-spreadsheets-fail',
    title: 'Why Spreadsheets Fail at Hotel Tech Management (And What to Use Instead)',
    excerpt: 'You\'ve got 30 properties, 400 vendor relationships, and a spreadsheet that was last updated... when? Here\'s why the spreadsheet era of hotel technology management is over.',
    category: 'Industry',
    date: 'Feb 2026',
    readTime: '5 min',
    content: `Every hotel management company starts the same way: someone creates a spreadsheet. "Hotel Tech Stack Master.xlsx" — columns for property, platform, vendor, contract date, notes. It works for 3 properties. At 10, it's unwieldy. At 30, it's fiction.

The problem isn't that spreadsheets are bad tools. They're great — for static data. Hotel technology is anything but static. Integrations change. Vendors get acquired. Contracts auto-renew at last year's rate because nobody checked. A new brand standard drops and nobody knows which properties are compliant.

**The real cost isn't the spreadsheet. It's what you can't see:**
- Which integrations are actually active vs just "planned"?
- Who supports the PMS at your Hampton in Mobile at 2am?
- How many different POS vendors are you paying across the portfolio?
- Which vendor contracts renew in the next 90 days?

LodgiFlow was built to answer these questions in seconds, not hours. Interactive visualization maps. 31 reports. AI-powered analysis. Real-time governance tracking.

The spreadsheet had a good run. It's time for something better.`
  },
  {
    id: 'integration-gap-analysis',
    title: 'The Hidden Cost of Integration Gaps in Your Hotel Tech Stack',
    excerpt: 'That missing PMS-to-housekeeping integration isn\'t just an inconvenience — it\'s costing you $X per room per year in manual workarounds.',
    category: 'Technology',
    date: 'Feb 2026',
    readTime: '7 min',
    content: `When your PMS doesn't talk to your housekeeping system, someone has to manually update room status. When your CRS doesn't sync with your revenue management tool, someone has to export and import rates. Every missing integration creates a manual process — and every manual process creates errors, delays, and labor costs.

**The Two-Layer Integration Problem**

Most management companies track integrations at the platform level: "PMS should connect to CRS." But the reality is more nuanced. StayNTouch might integrate with SynXis, but Opera might not. The platform-level template says "connected" — the product-level reality says "gap."

LodgiFlow tracks both layers. Platform integrations define what SHOULD connect. Product integrations define what ACTUALLY does. The gap between them is where your hidden costs live.

**Quantifying the Gap**

For a typical 200-room full-service hotel with 20 platform connections:
- Each missing integration = ~2 hours/week of manual work
- At $25/hour burdened labor cost
- 5 missing integrations = $13,000/year in manual workarounds

Multiply by 30 properties and you're looking at $390,000/year in labor that exists solely because systems don't talk to each other.

LodgiFlow's integration gap analysis shows you every missing connection — color-coded on the map. Green means connected. Red means money leaking.`
  },
  {
    id: 'brand-compliance-guide',
    title: 'A Practical Guide to Hotel Brand Technology Compliance',
    excerpt: 'Hilton requires different systems than Marriott. Hampton has different requirements than Hilton main. Here\'s how to track it all without losing your mind.',
    category: 'Compliance',
    date: 'Mar 2026',
    readTime: '6 min',
    content: `Hotel brand compliance isn't optional — it's contractual. When Hilton says "Brand Required," they mean it. When Marriott says "Not Allowed," they enforce it. And when your management company operates across multiple brands, tracking which solutions are required, approved, or prohibited at each property becomes a full-time job.

**The Sub-Brand Complexity**

It's not just "Hilton vs Marriott." It's Hampton vs Hilton Garden Inn vs DoubleTree vs Conrad. Each sub-brand can have different technology requirements. A PMS that's required for Hampton might only be "approved" for HGI. A guest messaging platform that's brand-standard for full-service Hiltons might not be required for focused-service brands.

**What Good Compliance Tracking Looks Like**

1. Per-product designation: Required, Approved, Not Allowed — per brand AND sub-brand
2. Per-property compliance score: "This Hampton Inn uses 85% brand-required solutions"
3. Gap detail: "This property is missing 3 brand-required platforms"
4. Portfolio rollup: "78% overall brand compliance across 30 properties"

LodgiFlow does all of this automatically. Set your brand standards once — compliance scores update in real-time as you add or change solutions.`
  },
  {
    id: 'ai-hotel-technology',
    title: 'How AI is Changing Hotel Technology Management in 2026',
    excerpt: 'From parsing vendor integration lists to predicting which platforms you\'ll need next — AI is eliminating the grunt work of hotel tech management.',
    category: 'AI & Innovation',
    date: 'Mar 2026',
    readTime: '8 min',
    content: `The hospitality industry is famously slow to adopt new technology. But AI isn't asking for permission — it's making hotel tech management fundamentally easier, faster, and smarter.

**The Manual Work That AI Eliminates**

1. **Integration Mapping**: A vendor sends you their integration list as a PDF. Someone spends 2 hours cross-referencing it with your platform types. AI does it in 10 seconds.

2. **Contract Analysis**: A 15-page vendor agreement. Someone reads every clause looking for auto-renew terms, exit fees, and SLA commitments. AI extracts every key term instantly.

3. **Anomaly Detection**: Something's off at one of your 30 properties — but which one? And what? AI scans your entire portfolio and surfaces: "Hampton PCB has 0% SSO coverage. That's unusual for a Hilton brand."

4. **Tech Stack Recommendations**: A new property joins your portfolio. What platforms should it have? AI analyzes its attributes (350 rooms, full-service, 4 F&B outlets) and recommends specific products ranked by deployment popularity and integration compatibility.

**This Isn't Science Fiction**

LodgiFlow ships with 9 AI assistants today. They're not gimmicks — each one replaces a specific manual process that hotel tech teams do every week. Integration mapping. Contract parsing. RFP writing. Vendor comparison. Cost optimization. Anomaly detection. Weekly digest generation.

The technology teams that adopt AI tooling now will manage twice the portfolio with the same headcount. The ones that don't will keep spending their Mondays updating spreadsheets.`
  },
];

export default function BlogPage({ onNavigate }) {
  const [selectedPost, setSelectedPost] = useState(null);

  if (selectedPost) {
    const post = BLOG_POSTS.find(p => p.id === selectedPost);
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <button onClick={() => setSelectedPost(null)} className="flex items-center gap-2 text-sm text-[#0055FF] hover:underline mb-8">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Blog
        </button>
        <Badge className="mb-4 text-xs bg-blue-50 text-blue-700 border-blue-200">{post.category}</Badge>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk' }}>{post.title}</h1>
        <div className="flex items-center gap-4 text-sm text-[#737373] mb-8">
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readTime} read</span>
          <span>{post.date}</span>
        </div>
        <div className="prose prose-slate max-w-none [&_p]:text-[#525252] [&_p]:leading-relaxed [&_p]:mb-4 [&_strong]:text-[#0A0A0A] [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-3 [&_li]:text-[#525252]">
          {post.content.split('\n\n').map((para, i) => {
            if (para.startsWith('**') && para.endsWith('**')) return <h2 key={i}>{para.replace(/\*\*/g, '')}</h2>;
            if (para.includes('\n-') || para.startsWith('-')) {
              const items = para.split('\n').filter(l => l.startsWith('-'));
              return <ul key={i} className="list-disc pl-6 mb-4">{items.map((item, j) => <li key={j}>{item.slice(2)}</li>)}</ul>;
            }
            return <p key={i} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />;
          })}
        </div>
        <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 text-center">
          <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Ready to see LodgiFlow in action?</h3>
          <p className="text-sm text-[#737373] mb-4">14-day free trial. No credit card required.</p>
          <Button className="gap-2 bg-[#0055FF] hover:bg-[#0044DD]" onClick={() => onNavigate('signup')}>Start Free Trial <ArrowRight className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="blog-page">
      <p className="text-xs tracking-[0.2em] uppercase font-mono text-[#0055FF] mb-3">Blog</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'Space Grotesk' }}>Insights for portfolio technology leaders</h1>
      <p className="text-[#737373] mb-12 max-w-2xl">Practical guides, industry analysis, and product updates for hotel management companies that take technology seriously.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {BLOG_POSTS.map(post => (
          <button key={post.id} onClick={() => setSelectedPost(post.id)}
            className="text-left p-6 rounded-2xl border border-[#E5E5E5] bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
            <Badge className="mb-3 text-[10px] bg-blue-50 text-blue-700 border-blue-200">{post.category}</Badge>
            <h2 className="text-lg font-semibold mb-2 group-hover:text-[#0055FF] transition-colors" style={{ fontFamily: 'Space Grotesk' }}>{post.title}</h2>
            <p className="text-sm text-[#737373] leading-relaxed mb-4">{post.excerpt}</p>
            <div className="flex items-center gap-3 text-xs text-[#A3A3A3]">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readTime}</span>
              <span>{post.date}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
